const DEFAULT_TIMEOUT_MS = 6000;
const DEFAULT_IDLE_TIMEOUT_MS = 1200;
const DEFAULT_INTERACTION_SETTLE_MS = 120;

const runWhenIdle = (callback, timeout = DEFAULT_IDLE_TIMEOUT_MS) => {
  if (typeof window === "undefined") {
    callback();
    return undefined;
  }

  if (typeof window.requestIdleCallback === "function") {
    const idleId = window.requestIdleCallback(callback, { timeout });
    return () => {
      if (typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
    };
  }

  const timeoutId = window.setTimeout(callback, 0);
  return () => window.clearTimeout(timeoutId);
};

export function scheduleAfterLcpIdleOrInteraction(callback, options = {}) {
  if (typeof window === "undefined") {
    callback();
    return undefined;
  }

  const timeoutMs = Number.isFinite(options.timeoutMs)
    ? options.timeoutMs
    : DEFAULT_TIMEOUT_MS;
  const idleTimeoutMs = Number.isFinite(options.idleTimeoutMs)
    ? options.idleTimeoutMs
    : DEFAULT_IDLE_TIMEOUT_MS;
  const lcpSettleMs = Number.isFinite(options.lcpSettleMs)
    ? options.lcpSettleMs
    : 900;
  const interactionSettleMs = Number.isFinite(options.interactionSettleMs)
    ? options.interactionSettleMs
    : DEFAULT_INTERACTION_SETTLE_MS;
  const deferInteractionUntilIdle = Boolean(options.deferInteractionUntilIdle);

  const cleanups = [];
  let disposed = false;
  let interactionRunScheduled = false;

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    cleanups.splice(0).forEach((cleanup) => cleanup());
  };

  const run = () => {
    if (disposed) return;
    dispose();
    callback();
  };

  const runOnIdle = () => {
    if (disposed) return;
    const cleanup = runWhenIdle(run, idleTimeoutMs);
    if (cleanup) cleanups.push(cleanup);
  };

  const runAfterInteractionPaint = () => {
    if (disposed || interactionRunScheduled) return;
    interactionRunScheduled = true;

    const scheduleIdle = () => {
      if (disposed) return;
      const timeoutId = window.setTimeout(runOnIdle, interactionSettleMs);
      cleanups.push(() => window.clearTimeout(timeoutId));
    };

    if (typeof window.requestAnimationFrame === "function") {
      let secondFrameId = null;
      const firstFrameId = window.requestAnimationFrame(() => {
        secondFrameId = window.requestAnimationFrame(scheduleIdle);
      });
      cleanups.push(() => {
        window.cancelAnimationFrame(firstFrameId);
        if (secondFrameId !== null) {
          window.cancelAnimationFrame(secondFrameId);
        }
      });
      return;
    }

    scheduleIdle();
  };

  const addOneShotListener = (target, type, listener, listenerOptions) => {
    target.addEventListener(type, listener, listenerOptions);
    cleanups.push(() => target.removeEventListener(type, listener, listenerOptions));
  };

  const runFromInteraction = deferInteractionUntilIdle
    ? runAfterInteractionPaint
    : run;

  addOneShotListener(window, "pointerdown", runFromInteraction, {
    passive: true,
    once: true,
  });
  addOneShotListener(window, "keydown", runFromInteraction, { once: true });
  addOneShotListener(window, "touchstart", runFromInteraction, {
    passive: true,
    once: true,
  });
  addOneShotListener(document, "visibilitychange", () => {
    if (document.visibilityState === "hidden") run();
  });

  if (typeof window.PerformanceObserver === "function") {
    try {
      let lcpTimerId = null;
      const observer = new window.PerformanceObserver((list) => {
        if (!list.getEntries().length || lcpTimerId !== null) return;
        lcpTimerId = window.setTimeout(runOnIdle, lcpSettleMs);
        cleanups.push(() => window.clearTimeout(lcpTimerId));
      });
      observer.observe({ type: "largest-contentful-paint", buffered: true });
      cleanups.push(() => observer.disconnect());
    } catch (_) {
      // Some browsers expose PerformanceObserver without LCP support.
    }
  }

  const timeoutId = window.setTimeout(runOnIdle, timeoutMs);
  cleanups.push(() => window.clearTimeout(timeoutId));

  return dispose;
}
